/**
 * Per-conversation message cache backed by localStorage.
 *
 * Why this exists
 * ───────────────
 * Each conversation-open call costs ~1 s against the remote MySQL host,
 * and the user sees an empty polka-dot field while the messages fetch.
 * iter134.3 ships an offline-first read path: hydrate immediately from
 * cache, then reconcile in the background. WS push events keep the
 * cache hot for the active conversation, so the next click is free.
 *
 * Storage layout
 * ──────────────
 * One key per conversation:
 *   usair_conv_msg:{conversation_id} → { updated_at: ISO, items: Message[] }
 * Plus an LRU index:
 *   usair_conv_msg_index → [{ cid, touched_at }, …]  newest first
 *
 * Bounds
 * ──────
 *   • Up to ``MAX_MESSAGES_PER_CONV`` (50) messages per conversation.
 *   • Up to ``MAX_CONVS_CACHED`` (50) conversations total — we evict
 *     the least-recently-touched convs first.
 *   • Soft-fails on any localStorage error (private mode, quota, …).
 *     Cache is a perf win, never a correctness path.
 */

const PREFIX = "usair_conv_msg:";
const INDEX_KEY = "usair_conv_msg_index";
const MAX_MESSAGES_PER_CONV = 50;
const MAX_CONVS_CACHED = 50;

// ── Internals ───────────────────────────────────────────────────

function safeGet(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function safeRemove(key) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

function readIndex() {
  return safeGet(INDEX_KEY) || [];
}

function writeIndex(idx) {
  safeSet(INDEX_KEY, idx);
}

function touch(cid) {
  const now = Date.now();
  const idx = readIndex().filter((e) => e.cid !== cid);
  idx.unshift({ cid, touched_at: now });
  // Evict overflow — drop oldest convs and their data.
  while (idx.length > MAX_CONVS_CACHED) {
    const dropped = idx.pop();
    if (dropped) safeRemove(PREFIX + dropped.cid);
  }
  writeIndex(idx);
}

// Sort by sequence_number, dedupe by message_id. Older messages first
// (chronological), matching the API response order.
function normalize(items) {
  const seen = new Map();
  for (const m of items || []) {
    if (!m || !m.message_id) continue;
    seen.set(m.message_id, m);
  }
  return Array.from(seen.values()).sort(
    (a, b) => (a.sequence_number || 0) - (b.sequence_number || 0),
  );
}

// ── Public API ─────────────────────────────────────────────────

/** Read the cached messages for a conversation, or null on miss. */
export function getCached(cid) {
  if (!cid) return null;
  const entry = safeGet(PREFIX + cid);
  if (!entry || !Array.isArray(entry.items)) return null;
  return entry.items;
}

/** Replace the cache for a conversation with the latest ``items``.
 *  Keeps only the newest ``MAX_MESSAGES_PER_CONV`` messages. */
export function setCached(cid, items) {
  if (!cid) return;
  const norm = normalize(items);
  const trimmed = norm.slice(-MAX_MESSAGES_PER_CONV);
  safeSet(PREFIX + cid, { updated_at: new Date().toISOString(), items: trimmed });
  touch(cid);
}

/** Append a single new message (e.g. WS ``message_new``) to the cache,
 *  trimming if the per-conv cap is exceeded. */
export function appendCached(cid, message) {
  if (!cid || !message || !message.message_id) return;
  const existing = getCached(cid) || [];
  if (existing.some((m) => m.message_id === message.message_id)) return;
  const next = normalize([...existing, message]).slice(-MAX_MESSAGES_PER_CONV);
  safeSet(PREFIX + cid, { updated_at: new Date().toISOString(), items: next });
  touch(cid);
}

/** Patch an existing cached message in place — used for ``message_edited``
 *  and ``message_recalled`` so the next render pulls the new content. */
export function patchCached(cid, message_id, patch) {
  if (!cid || !message_id || !patch) return;
  const existing = getCached(cid);
  if (!existing) return;
  const next = existing.map((m) =>
    m.message_id === message_id ? { ...m, ...patch } : m,
  );
  safeSet(PREFIX + cid, { updated_at: new Date().toISOString(), items: next });
  touch(cid);
}

/** Drop every cached conversation. Called on logout. */
export function clearAll() {
  for (const e of readIndex()) safeRemove(PREFIX + e.cid);
  safeRemove(INDEX_KEY);
  for (const k of imgIndex()) safeRemove(IMG_PREFIX + k);
  safeRemove(IMG_INDEX_KEY);
}

// ── Image cache ──────────────────────────────────────────────────
//
// Stores small chat attachments as base64 in localStorage so the
// next conversation-open paints them without a network round-trip.
// Bounded tightly (default 256 KB per image, 5 MB total) because
// localStorage quotas are typically ~5 MB browser-wide and we MUST
// share that budget with the message cache + portal state.
//
// Misses are silent — AuthImage falls back to a network fetch and
// then offers the resulting blob back to ``setCachedImage`` for the
// next visit.

const IMG_PREFIX = "usair_img:";
const IMG_INDEX_KEY = "usair_img_index";
const MAX_BYTES_PER_IMAGE = 256 * 1024;     // 256 KB
const MAX_IMAGE_CACHE_BYTES = 5 * 1024 * 1024;  // 5 MB total budget

function imgIndex() {
  const idx = safeGet(IMG_INDEX_KEY);
  return Array.isArray(idx) ? idx : [];
}

function imgKey(src) {
  // The ``src`` IS the natural cache key (e.g. ``/conv/files/{message_id}``).
  // Hash-prefix collisions are not a concern at this scale.
  return src;
}

/** Convert a Blob to a data URL string. Wrapped in a Promise so the
 *  caller can ``await`` the encoding step. */
export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

/** Read a cached image's data URL, or null on miss. Touches the LRU
 *  index so the entry survives the next eviction sweep. */
export function getCachedImage(src) {
  if (!src) return null;
  const key = imgKey(src);
  const entry = safeGet(IMG_PREFIX + key);
  if (!entry || typeof entry.data_url !== "string") return null;
  // LRU touch.
  const idx = imgIndex().filter((e) => e.k !== key);
  idx.unshift({ k: key, b: entry.bytes || 0, t: Date.now() });
  safeSet(IMG_INDEX_KEY, idx);
  return entry.data_url;
}

/** Store an image's data URL. Skips images larger than the per-item
 *  cap and evicts oldest entries when total goes over the budget. */
export function setCachedImage(src, dataUrl) {
  if (!src || !dataUrl) return false;
  const bytes = dataUrl.length;
  if (bytes > MAX_BYTES_PER_IMAGE) return false;
  const key = imgKey(src);
  // Make room: drop oldest entries until we're under the total budget.
  let idx = imgIndex().filter((e) => e.k !== key);
  let total = idx.reduce((s, e) => s + (e.b || 0), 0) + bytes;
  while (total > MAX_IMAGE_CACHE_BYTES && idx.length > 0) {
    const dropped = idx.pop();
    if (dropped) {
      safeRemove(IMG_PREFIX + dropped.k);
      total -= dropped.b || 0;
    }
  }
  if (!safeSet(IMG_PREFIX + key, { data_url: dataUrl, bytes })) return false;
  idx.unshift({ k: key, b: bytes, t: Date.now() });
  safeSet(IMG_INDEX_KEY, idx);
  return true;
}
